import React, { useEffect, useMemo, useState } from 'react'
import Card from '../../../components/ui/Card'
import Button from '../../../components/ui/Button'
import Icon from '../../../components/AppIcon'
import { FRAGRANCE_FIELDS, isFragrance } from '../utils/fragranceDraft.mjs'
import { useToast } from '../../../lib/ToastContext'

const TAG_PRINT_INFO_MAX = 24
const METAFIELD_KEYS = [
  ...Object.keys(FRAGRANCE_FIELDS),
  'material',
  'karat',
  'stone_type',
  'stone_carat',
  'weight',
  'weight_unit',
  'ring_size',
  'supplier_name',
  'supplier_product_number',
  'tag_print_info'
]

const METAFIELD_LABELS = {
  ...FRAGRANCE_FIELDS,
  material: 'Material',
  karat: 'Karat',
  stone_type: 'Stone Type',
  stone_carat: 'Stone Carat',
  weight: 'Weight',
  weight_unit: 'Weight Unit',
  ring_size: 'Ring Size',
  supplier_name: 'Supplier Name',
  supplier_product_number: 'Supplier Product #',
  tag_print_info: 'Tag Print Info'
}

function formatReviewField(field) {
  return String(field || '')
    .replace(/^metafields\./, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function normalizeReviewItems(product) {
  const reviewFlags = Array.isArray(product?.reviewFlags) ? product.reviewFlags : []
  const missingFields = Array.isArray(product?.missingFields) ? product.missingFields : []
  const items = reviewFlags
    .filter(Boolean)
    .map((item) => ({
      field: item.field || '',
      label: item.label || formatReviewField(item.field),
      reason: item.reason || 'Needs manual review.',
      severity: item.severity === 'required' ? 'required' : 'recommended'
    }))

  const seen = new Set(items.map((item) => item.field).filter(Boolean))
  missingFields.forEach((field) => {
    const normalizedField = String(field || '').trim()
    if (!normalizedField || seen.has(normalizedField)) return
    items.push({
      field: normalizedField,
      label: formatReviewField(normalizedField),
      reason: 'Still unresolved in this draft.',
      severity: 'required'
    })
  })

  return items
}

function normalizeTags(value) {
  if (Array.isArray(value)) return value.filter(Boolean).join(', ')
  return String(value || '')
}

function parseTags(value) {
  return String(value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean)
}

function toNumberOrNull(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const parsed = Number.parseFloat(raw.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : null
}

function toInput(value) {
  if (value === undefined || value === null) return ''
  return String(value)
}

function buildFormData(product) {
  return {
    title: toInput(product?.title),
    description: toInput(product?.description || product?.originalDescription),
    productType: toInput(product?.productType),
    category: toInput(product?.category),
    subcategory: toInput(product?.subcategory),
    tags: normalizeTags(product?.tags),
    costPrice: toInput(product?.costPrice),
    salePrice: toInput(product?.salePrice),
    compareAtPrice: toInput(product?.compareAtPrice),
    weight: toInput(product?.weight),
    sku: toInput(product?.sku),
    barcode: toInput(product?.barcode),
    metafields: { ...(product?.metafields || {}) }
  }
}

const PendingProductEditPanel = ({
  product,
  onSave,
  onDelete,
  onClose
}) => {
  const toast = useToast()
  const [formData, setFormData] = useState(() => buildFormData(product))
  const [saving, setSaving] = useState(false)

  const originalData = useMemo(() => buildFormData(product), [product])
  const reviewItems = useMemo(() => normalizeReviewItems(product), [product])

  useEffect(() => {
    setFormData(buildFormData(product))
  }, [product?.id])

  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData)
  }, [formData, originalData])

  const updateField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }))
  }

  const updateMetafield = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      metafields: {
        ...(prev.metafields || {}),
        [key]: value
      }
    }))
  }

  const handleSave = async () => {
    if (!isDirty || !onSave) return
    setSaving(true)
    try {
      const nextWeight = toInput(formData.weight).trim()
      const nextTagPrintInfo = toInput(formData.metafields?.tag_print_info).slice(0, TAG_PRINT_INFO_MAX)
      const payload = {
        title: formData.title.trim(),
        description: formData.description,
        originalDescription: formData.description,
        productType: formData.productType.trim(),
        category: formData.category.trim() || null,
        subcategory: formData.subcategory.trim() || null,
        tags: parseTags(formData.tags),
        costPrice: toNumberOrNull(formData.costPrice),
        salePrice: toNumberOrNull(formData.salePrice),
        compareAtPrice: toNumberOrNull(formData.compareAtPrice),
        weight: toNumberOrNull(formData.weight),
        metafields: {
          ...(formData.metafields || {}),
          weight: toInput(formData.metafields?.weight).trim() || nextWeight || '',
          tag_print_info: nextTagPrintInfo
        }
      }
      await onSave(product.id, payload)
      toast.success('Pending product updated')
    } catch (err) {
      console.error('Failed to update pending product:', err)
      toast.error('Failed to update pending product')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card variant="solid" className="border border-border/60 shadow-elevation-1 max-h-[calc(100vh-220px)] overflow-y-auto">
      <div className="flex items-center justify-between mb-4 sticky top-0 bg-card pb-3 border-b border-border/60 z-10">
        <div className="min-w-0">
          <h3 className="font-semibold text-foreground truncate text-sm">
            {formData.title || 'Edit Pending Product'}
          </h3>
          <p className="text-xs text-muted-foreground">You can edit product info before final photo workflow.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
        >
          <Icon name="X" size={18} />
        </button>
      </div>

      <div className="space-y-4">
        {reviewItems.length > 0 && (
          <div className="rounded-lg border border-warning/30 bg-warning/10 p-3">
            <div className="flex items-center gap-2 mb-2">
              <Icon name="ClipboardList" size={15} className="text-warning" />
              <h4 className="text-sm font-semibold text-foreground">Review Checklist</h4>
            </div>
            <div className="space-y-2">
              {reviewItems.map((item, index) => (
                <div
                  key={`${item.field || 'review'}-${index}`}
                  className="rounded-md border border-warning/20 bg-background/80 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-foreground">{item.label || 'Manual review'}</p>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      item.severity === 'required'
                        ? 'bg-destructive/12 text-destructive'
                        : 'bg-warning/15 text-warning'
                    }`}>
                      {item.severity === 'required' ? 'Required' : 'Recommended'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{item.reason}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Title</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        <div>
          <label className="text-xs text-muted-foreground block mb-1">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={5}
            className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Product Type</label>
            <input
              type="text"
              value={formData.productType}
              onChange={(e) => updateField('productType', e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Tags (comma separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => updateField('tags', e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Sale Price</label>
            <input
              type="number"
              step="0.01"
              value={formData.salePrice}
              onChange={(e) => updateField('salePrice', e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Compare At</label>
            <input
              type="number"
              step="0.01"
              value={formData.compareAtPrice}
              onChange={(e) => updateField('compareAtPrice', e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Weight (g)</label>
            <input
              type="number"
              step="0.01"
              value={formData.weight}
              onChange={(e) => updateField('weight', e.target.value)}
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-muted-foreground block mb-1">SKU (locked)</label>
            <input
              type="text"
              value={formData.sku}
              readOnly
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm font-mono cursor-not-allowed"
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground block mb-1">Barcode (locked)</label>
            <input
              type="text"
              value={formData.barcode}
              readOnly
              className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm font-mono cursor-not-allowed"
            />
          </div>
        </div>

        <div className="border border-border/60 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-foreground">Metafields</h4>
            <span className="text-[11px] text-muted-foreground">
              {(formData.metafields?.tag_print_info || '').length}/{TAG_PRINT_INFO_MAX}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {METAFIELD_KEYS.filter(key => isFragrance(product) ? !['material','karat','stone_type','stone_carat','ring_size'].includes(key) : !(key in FRAGRANCE_FIELDS && key !== 'brand')).map((key) => (
              <div key={key}>
                <label className="text-xs text-muted-foreground block mb-1">{METAFIELD_LABELS[key] || key}</label>
                <input
                  type="text"
                  value={toInput(formData.metafields?.[key])}
                  onChange={(e) => {
                    const raw = e.target.value
                    updateMetafield(
                      key,
                      key === 'tag_print_info' ? raw.slice(0, TAG_PRINT_INFO_MAX) : raw
                    )
                  }}
                  className="w-full px-3 py-2 bg-input text-foreground border border-border/60 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-border/60">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete?.(product.id)}
          >
            <Icon name="Trash2" size={14} className="mr-1" />
            Delete Product
          </Button>
          <div className="flex-1" />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFormData(buildFormData(product))}
            disabled={!isDirty || saving}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            disabled={!isDirty || saving}
          >
            <Icon name={saving ? 'Loader2' : 'Check'} size={14} className={`mr-1 ${saving ? 'animate-spin' : ''}`} />
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Card>
  )
}

export default PendingProductEditPanel
